import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { createRepository } from '../../../dto/repositoryFactory';
import {
  IJob,
  IListJobFetchParams,
  IListOrderFetchParams,
  IOrder,
} from '../../../dto';
import {
  ApiResponse,
  IListResponse,
  MN_JOBS,
  MN_ORDERS,
} from '../../../common';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../../utils';
import { ITimesheet } from '../../../dto';
import { useAuth } from '@jobcost-monorepo/ui';
import { MarkTypeEnum } from '@jobcost-monorepo/database/prisma/enums';

const HookComponentTimesheet: React.FC = () => {
  const { user } = useAuth();
  const { getValues, setValue, watch, formState } =
    useFormContext<ITimesheet>();
  const jobId = watch('jobId');
  const resourceId = watch('resourceId');
  const markType = watch('markType');

  const handleChangeResourceId = async () => {
    if (resourceId === null || resourceId === undefined) {
      handleResetResourceId();
      return;
    }

    if (formState.dirtyFields['resourceId']) {
      handleResetResourceId();
      const operationDate = getValues('operationDate');
      const repository = createRepository<IJob, IListJobFetchParams>(MN_JOBS);
      const job = await repository
        .getMany({
          resourceId,
          startDate: String(operationDate),
          endDate: String(operationDate),
          state: 'Running',
          orderState: 'Running',
        })
        .then((response: ApiResponse<IListResponse<IJob>>) => {
          if (response?.data && response?.data.data?.length === 1) {
            return response?.data.data[0];
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));

      if (job) {
        setValue('jobId', job.id, { shouldDirty: true });
      }
    }
  };

  const handleChangeJobId = async () => {
    if (jobId === null || jobId === undefined) {
      setValue('orderId', null);
      return;
    }

    if (formState.dirtyFields['jobId']) {
      const operationDate = getValues('operationDate');

      const repository = createRepository<IOrder, IListOrderFetchParams>(
        MN_ORDERS,
      );
      const order = await repository
        .getMany({
          resourceId,
          jobId,
          startDate: String(operationDate),
          endDate: String(operationDate),
          jobState: 'Running',
        })
        .then((response: ApiResponse<IListResponse<IOrder>>) => {
          if (response?.data && response?.data.data?.length === 1) {
            return response?.data.data[0];
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));

      if (order) {
        setValue('orderId', order.id, { shouldDirty: true });
        setValue('workedHours', order.hoursAllocatedPerDay, {
          shouldDirty: true,
        });
      }
    }
  };

  const handleChangeMarkType = () => {
    if (markType !== MarkTypeEnum.WorkedHours) {
      handleResetResourceId();
      return;
    }
  };

  const handleResetResourceId = () => {
    setValue('jobId', null);
    setValue('orderId', null);
  };

  useEffect(() => {
    handleChangeJobId().then();
  }, [jobId]);

  useEffect(() => {
    handleChangeResourceId().then();
  }, [resourceId]);

  useEffect(() => {
    handleChangeMarkType();
  }, [markType]);

  useEffect(() => {
    setValue('markType', 'WorkedHours');
  }, []);

  useEffect(() => {
    if (user && user.role === 'USER') {
      setValue('resourceId', Number(user?.resource?.id));
    }
  }, [user]);

  return <div />;
};

export default HookComponentTimesheet;
